import { useState, useEffect } from 'react';
import type { User } from '@maxiron/shared';
import { checkPermissionsBatch } from '../../lib/opa-client';
import { apiClient } from '../../lib/api-client';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { menuStructure, type MenuItem } from './nav-menu-structure';

export function useSidebarNavPermissions(user: User | null) {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const [pendingApprovals, setPendingApprovals] = useState<number>(0);

  useEffect(() => {
    if (!user) {
      setVisibleItems(new Set());
      return;
    }

    const allItems: MenuItem[] = [];
    menuStructure.forEach((item) => {
      if ('items' in item) {
        allItems.push(...item.items);
      } else {
        allItems.push(item);
      }
    });

    const checks = allItems
      .filter((item) => item.page)
      .map((item) => ({ page: item.page!, action: 'read' as const }));

    void checkPermissionsBatch(user, checks).then((results) => {
      const visible = new Set<string>();
      allItems.forEach((item) => {
        if (!item.page) {
          visible.add(item.path);
        } else {
          const key = `${item.page}:read`;
          if (results[key]) {
            visible.add(item.path);
          }
        }
      });
      setVisibleItems(visible);
    });
  }, [user]);

  const refreshPendingApprovals = async () => {
    if (!user?.email) return;
    try {
      const stats = await apiClient.getApprovalStats();
      const pending = Number(stats?.pending || 0);
      setPendingApprovals(Number.isFinite(pending) ? pending : 0);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!user) return;
    void refreshPendingApprovals();
  }, [user?.email]);

  useCacheEvents({
    enabled: true,
    cacheNames: ['approvals'],
    onEvent: (e) => {
      const email = String((user as any)?.email || '').trim().toLowerCase();
      const affected = Array.isArray(e.affectedUsers)
        ? e.affectedUsers.map((x) => String(x).trim().toLowerCase())
        : [];
      if (affected.length > 0 && email && !affected.includes(email)) return;
      void refreshPendingApprovals();
    },
  });

  return { visibleItems, pendingApprovals, refreshPendingApprovals };
}
